import { measureMintStage } from './ordinal-mint-diagnostics'
/**
 * Client-Side Signing Flow
 *
 * This module orchestrates the client-side transaction signing flow for self-custody.
 * When a user triggers an upvalue and has client-side signing enabled, the server
 * creates a signing request and broadcasts it via ActionCable. This module:
 *
 * 1. Receives the signing request (via ActionCable or polling)
 * 2. Uses the exact unsigned transaction template from the server
 * 3. Retrieves Shamir shares (client from IndexedDB, server from API)
 * 4. Recombines shares and signs the transaction
 * 5. Submits the signed transaction back to the server
 *
 * The server's Temporal workflow waits for the signed tx, validates it, and broadcasts.
 */

import api from '../api/api'
import {
    getClientShare,
    isSelfCustodyReinitializationRequiredError,
    recoverPaymentKeyWifWithShares,
    recoverLocalWalletTextWithShares,
    signTransaction,
    type ClientShareRecord,
    type UnsignedTransactionTemplate,
} from './bsv-signing'
import { signOrdinalTransfer, type OrdinalTransferManifest } from './ordinal-local-signing'
import { postAipIntentProblem, postAipOpsProblem, signPostAipOps } from './post-aip-signing'
import { clearOrdinalSendIntent, getOrdinalSendIntent } from './ordinal-send-intent'
import { signOrdinalMint, type OrdinalMintManifest } from './ordinal-local-mint-signing'
import {
    clearOrdinalMintIntent,
    contentSourceForOrdinalMintIntent,
    getOrdinalMintIntent,
} from './ordinal-mint-intent'
import {
    clearOwnerAttachmentWriteIntent,
    getOwnerAttachmentWriteIntent,
    signOwnerAttachmentWrite,
    type OwnerAttachmentWriteManifest,
} from './owner-attachment-local-signing'
import {
    persistOrdinalMarketSigningIntent,
    signOrdinalMarketInputs,
    type OrdinalMarketApproval,
    type OrdinalMarketManifest,
} from './ordinal-local-market-signing'
import {
    clearOrdinalMarketIntent,
    loadOrdinalMarketIntentForRequest,
} from './ordinal-market-intent'
import {
    bindProvisionalOrdinalMarketActionIntent,
    clearOrdinalMarketActionIntent,
    loadOrdinalMarketActionIntent,
    ordinalMarketActionMatchesRequest,
} from './ordinal-market-action-intent'

// ============================================================================
// Types
// ============================================================================

/**
 * Signing request as received from ActionCable or polling
 */
export interface SigningRequest {
    signing_request_id: string
    tx_type: string
    device_id: string
    unsigned_tx_template?: UnsignedTransactionTemplate
    ordinal_manifest?: OrdinalTransferManifest | OrdinalMintManifest | OwnerAttachmentWriteManifest
    ordinal_id?: string
    answer_id?: string
    attachment_id?: string
    market_manifest?: OrdinalMarketManifest
    market_approval?: OrdinalMarketApproval
    author_address?: string
    treechat_address?: string
    total_amount?: number
    author_amount?: number
    treechat_amount?: number
    is_boost?: boolean
    /** post_aip: byte-final bSocial ops to sign as the post's AIP author tuple */
    post_aip_ops?: string[]
    /** post_aip: the payment address the signature must verify against */
    aip_address?: string
    expires_at: string
}

/**
 * Result of processing a signing request
 */
export interface SigningFlowResult {
    success: boolean
    error?: string
    /** Whether the error is retryable */
    retryable?: boolean
}

// ============================================================================
// State
// ============================================================================

/** Tracks requests currently being processed to prevent double-signing */
const processingRequests = new Set<string>()
const volatileMarketIntents = new Map<string, string>()
const volatileMarketIntentStorage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> = {
    getItem: key => volatileMarketIntents.get(key) ?? null,
    setItem: (key, value) => volatileMarketIntents.set(key, value),
    removeItem: key => void volatileMarketIntents.delete(key),
}
let selectedMarketIntentStorage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null = null

const marketIntentStorage = (): Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> => {
    if (selectedMarketIntentStorage) return selectedMarketIntentStorage
    try {
        const storage = globalThis.localStorage
        if (!storage) throw new Error('localStorage is unavailable')
        storage.getItem('treechat:ordinal-market-intent:storage-probe')
        selectedMarketIntentStorage = storage
    } catch {
        selectedMarketIntentStorage = volatileMarketIntentStorage
    }
    return selectedMarketIntentStorage
}

const loadMarketIntentForRequest = (
    manifest: OrdinalMarketManifest,
    approval: OrdinalMarketApproval,
) => {
    let storage = marketIntentStorage()
    try {
        return loadOrdinalMarketIntentForRequest(
            manifest.authorizationId,
            approval.approvalRole,
            approval.signingRequestId,
            approval.attemptId,
            { storage },
        )
    } catch {
        selectedMarketIntentStorage = volatileMarketIntentStorage
        storage = volatileMarketIntentStorage
        return loadOrdinalMarketIntentForRequest(
            manifest.authorizationId,
            approval.approvalRole,
            approval.signingRequestId,
            approval.attemptId,
            { storage },
        )
    }
}

const persistMarketIntent = async (
    manifest: OrdinalMarketManifest,
    approval: OrdinalMarketApproval,
    maxNetworkFeeSatoshis: number,
) => {
    const storage = marketIntentStorage()
    try {
        return await persistOrdinalMarketSigningIntent(manifest, approval, maxNetworkFeeSatoshis, {
            storage,
        })
    } catch (error) {
        if (storage === volatileMarketIntentStorage) throw error
        selectedMarketIntentStorage = volatileMarketIntentStorage
        return persistOrdinalMarketSigningIntent(manifest, approval, maxNetworkFeeSatoshis, {
            storage: volatileMarketIntentStorage,
        })
    }
}

const clearMarketIntent = (manifest: OrdinalMarketManifest, approval: OrdinalMarketApproval) => {
    const storage = marketIntentStorage()
    try {
        clearOrdinalMarketIntent(manifest.authorizationId, approval.approvalRole, storage)
    } catch {
        selectedMarketIntentStorage = volatileMarketIntentStorage
        clearOrdinalMarketIntent(
            manifest.authorizationId,
            approval.approvalRole,
            volatileMarketIntentStorage,
        )
    }
}

/** Polling interval handle (if active) */
let pollingIntervalId: ReturnType<typeof setInterval> | null = null

/** Polling interval in milliseconds */
const POLLING_INTERVAL_MS = 45000

const hasClientSigningFeature = (): boolean => {
    const features = gon.currentUser?.features || []
    return features.some(
        feature => (typeof feature === 'string' ? feature : feature?.name) === 'use_client_signing',
    )
}

// ============================================================================
// Core Signing Flow
// ============================================================================

/**
 * Processes a signing request: builds, signs, and submits the transaction.
 *
 * @param request - The signing request from ActionCable or polling
 * @returns Result indicating success or failure
 */
export async function processSigningRequest(request: SigningRequest): Promise<SigningFlowResult> {
    const result = await signRequest(request)
    if (!result.success) reportSigningFailure(request, result)
    return result
}

// Failures already reported this page session: polling retries every 45s.
const reportedFailures = new Set<string>()

// The server otherwise sees only an expired request, so a device that refused or broke
// looks the same as a device that was closed. Requests meant for another device are
// skipped silently: every open tab of the user gets them.
function reportSigningFailure(request: SigningRequest, result: SigningFlowResult) {
    const error = result.error || 'unknown'
    if (error === 'Signing request targeted at a different device') return
    const key = `${request.signing_request_id}:${error}`
    if (reportedFailures.has(key)) return
    reportedFailures.add(key)
    api.reportSigningFailure(request.signing_request_id, request.device_id, {
        error,
        retryable: result.retryable === true,
        visible: typeof document !== 'undefined' && document.visibilityState === 'visible',
    }).catch(() => {})
}

async function signRequest(request: SigningRequest): Promise<SigningFlowResult> {
    const requestId = request.signing_request_id

    // Prevent double-processing
    if (processingRequests.has(requestId)) {
        console.log(`>>> Signing request ${requestId} already being processed, skipping`)
        return { success: true } // Not an error, just already handled
    }

    processingRequests.add(requestId)

    try {
        console.log(`>>> Processing signing request ${requestId} for ${request.tx_type}`)

        // Check if request has expired
        const expiresAt = new Date(request.expires_at)
        if (Date.now() > expiresAt.getTime()) {
            console.log(`>>> Signing request ${requestId} has expired`)
            return { success: false, error: 'Signing request expired', retryable: false }
        }

        // Get user ID from gon
        const userId = gon.currentUser?.id
        if (!userId) {
            return { success: false, error: 'Not logged in', retryable: false }
        }

        if (!hasClientSigningFeature()) {
            return {
                success: false,
                error: 'Client signing is not enabled for this user',
                retryable: false,
            }
        }

        // Step 1: Get client share from IndexedDB
        const clientRecord = await getClientShare(userId)
        if (!clientRecord) {
            return {
                success: false,
                error: 'No client share found. Self-custody wallet not set up.',
                retryable: false,
            }
        }

        const deviceId = clientRecord.deviceId
        if (request.device_id !== deviceId) {
            console.log(
                `>>> Signing request ${requestId} targets device ${request.device_id}, skipping ${deviceId}`,
            )
            return {
                success: false,
                error: 'Signing request targeted at a different device',
                retryable: false,
            }
        }

        // Step 2: Get server share from API
        const serverShareResult = await api.getBsvServerShare(userId, deviceId)
        if (!serverShareResult) {
            return {
                success: false,
                error: 'Could not retrieve server share',
                retryable: true,
            }
        }

        if (request.tx_type === 'post_aip') {
            // Sign only a Treechat post by this user for the named post, with the
            // key behind the address the server will verify against.
            const problem =
                postAipOpsProblem(request.post_aip_ops, {
                    username: gon.currentUser?.name || '',
                    answerId: request.answer_id || '',
                }) ||
                (request.aip_address && request.aip_address === serverShareResult.address
                    ? null
                    : 'author address does not match this wallet') ||
                (await postAipIntentProblem(
                    String(userId),
                    request.answer_id || '',
                    request.post_aip_ops![1],
                ))
            if (problem) {
                return {
                    success: false,
                    error: `Refusing to sign post: ${problem}`,
                    retryable: false,
                }
            }
            const paymentKeyWif = await recoverPaymentKeyWifWithShares(
                userId,
                serverShareResult.treechatShare,
                clientRecord.clientShare,
                request.aip_address,
            )
            const signature = signPostAipOps(request.post_aip_ops!, paymentKeyWif)
            const submitted = await api.submitSignedTx(requestId, deviceId, signature)
            return submitted.success
                ? { success: true }
                : {
                      success: false,
                      error: submitted.error || 'Failed to submit post signature',
                      retryable: true,
                  }
        }

        if (request.tx_type.startsWith('ordinal_market_')) {
            const manifest = request.market_manifest
            const approval = request.market_approval
            if (
                !manifest ||
                !approval ||
                approval.signingRequestId !== requestId ||
                approval.userId !== String(userId) ||
                approval.deviceId !== deviceId
            ) {
                return {
                    success: false,
                    error: 'Ordinal market signing request is missing or mismatched',
                    retryable: false,
                }
            }
            const expectedAction = {
                version: 1 as const,
                operation: manifest.operation,
                ordinalId: String(request.ordinal_id),
                priceSats: manifest.priceSats,
                treechatFeeSats: manifest.treechatFee.satoshis,
                sellerProceedsSats: manifest.sellerProceedsSats,
                userId: String(userId),
                deviceId,
                createdAt: Date.now(),
            }
            const action =
                loadOrdinalMarketActionIntent(manifest.orderId) ||
                bindProvisionalOrdinalMarketActionIntent(manifest.orderId, expectedAction)
            if (!action) {
                return {
                    success: false,
                    error: 'Waiting for the confirmed ordinal market action',
                    retryable: true,
                }
            }
            if (
                !ordinalMarketActionMatchesRequest(
                    action.operation,
                    manifest.operation,
                    approval.approvalRole,
                ) ||
                action.ordinalId !== request.ordinal_id ||
                action.priceSats !== manifest.priceSats ||
                action.treechatFeeSats !== manifest.treechatFee.satoshis ||
                action.sellerProceedsSats !== manifest.sellerProceedsSats ||
                action.userId !== String(userId) ||
                action.deviceId !== deviceId
            ) {
                return {
                    success: false,
                    error: "Ordinal market request does not match this device's confirmed action",
                    retryable: false,
                }
            }

            let intent = loadMarketIntentForRequest(manifest, approval)
            if (!intent) {
                const outputScriptBytes = manifest.outputs.reduce(
                    (sum, output) => sum + output.script.length / 2,
                    0,
                )
                const estimatedBytes =
                    10 +
                    manifest.inputs.length * 180 +
                    manifest.outputs.length * 10 +
                    outputScriptBytes
                const maxNetworkFeeSatoshis = Math.max(1_000, estimatedBytes * 20)
                intent = await persistMarketIntent(manifest, approval, maxNetworkFeeSatoshis)
            }

            const walletText = await recoverLocalWalletTextWithShares(
                userId,
                serverShareResult.treechatShare,
                clientRecord.clientShare,
            )
            const wallet = JSON.parse(walletText)
            // A seller ordinal without an inline script streams from the server; a
            // listing purchase needs the stored parent transaction instead.
            const streamsOrdinalPrevout = approval.assignedInputIndexes.some(
                index =>
                    manifest.inputs[index]?.role === 'seller_ordinal' &&
                    !manifest.inputs[index]?.scriptPubKey,
            )
            const ordinalPrevoutScript = streamsOrdinalPrevout
                ? await api.openSigningRequestOrdinalPrevoutScript(requestId, deviceId)
                : undefined
            const context =
                manifest.operation === 'ordinal_market_listing_purchase'
                    ? await api.getSigningRequestMarketContext(requestId, deviceId)
                    : {}
            const signatures = await signOrdinalMarketInputs({
                manifest,
                approval,
                intent,
                expectedUserId: String(userId),
                expectedDeviceId: deviceId,
                ordinalPrevoutScript,
                parentTransactionHex: context.parent_transaction_hex,
                payPk: wallet.payPk,
                ordPk: wallet.ordPk,
            })
            const submitted = await api.submitOrdinalSignatures(requestId, deviceId, signatures)
            if (submitted.success) {
                clearMarketIntent(manifest, approval)
                clearOrdinalMarketActionIntent(manifest.orderId)
                return { success: true }
            }
            return {
                success: false,
                error: submitted.error || 'Failed to submit ordinal market signatures',
                retryable: true,
            }
        }

        if (request.tx_type === 'ordinal_mint') {
            const manifest = request.ordinal_manifest as OrdinalMintManifest | undefined
            if (
                !manifest ||
                manifest.operation !== 'ordinal_mint' ||
                manifest.signingRequestId !== requestId
            ) {
                return {
                    success: false,
                    error: 'Ordinal mint signing manifest is missing or mismatched',
                    retryable: false,
                }
            }
            const answerId = request.answer_id || manifest.answerId
            const attachmentId = request.attachment_id || manifest.attachmentId
            const intent = getOrdinalMintIntent(answerId, attachmentId)
            if (
                !intent ||
                intent.userId !== String(userId) ||
                intent.deviceId !== deviceId ||
                intent.answerId !== manifest.answerId ||
                intent.attachmentId !== manifest.attachmentId
            ) {
                return {
                    success: false,
                    error: "Ordinal mint request does not match this device's mint action",
                    retryable: false,
                }
            }
            const walletText = await recoverLocalWalletTextWithShares(
                userId,
                serverShareResult.treechatShare,
                clientRecord.clientShare,
            )
            const wallet = JSON.parse(walletText)
            if (!wallet.payPk) {
                return {
                    success: false,
                    error: 'Local wallet is missing its payment signing key',
                    retryable: false,
                }
            }
            const content = contentSourceForOrdinalMintIntent(intent, api.getMintMediaContentChunk)
            const diagnosticContext = {
                answer_id: answerId,
                attachment_id: attachmentId,
                signing_request_id: requestId,
                content_bytes: content.size,
                source: content.mode,
            }
            const signatures = await measureMintStage(diagnosticContext, 'sign', observe =>
                signOrdinalMint(manifest, intent, content, wallet.payPk, observe),
            )
            const submitted = await measureMintStage(
                diagnosticContext,
                'submit',
                () => api.submitOrdinalSignatures(requestId, deviceId, signatures),
                result => result.success,
            )
            if (submitted.success) {
                clearOrdinalMintIntent(intent.answerId, intent.attachmentId)
                return { success: true }
            }
            return {
                success: false,
                error: submitted.error || 'Failed to submit ordinal mint signatures',
                retryable: true,
            }
        }

        if (request.tx_type === 'owner_attachment_write') {
            const manifest = request.ordinal_manifest as OwnerAttachmentWriteManifest | undefined
            if (
                !manifest ||
                manifest.operation !== 'owner_attachment_write' ||
                manifest.signingRequestId !== requestId
            ) {
                return {
                    success: false,
                    error: 'Onchain write signing manifest is missing or mismatched',
                    retryable: false,
                }
            }
            const intent = getOwnerAttachmentWriteIntent(manifest.answerId, manifest.attachmentId)
            if (!intent || intent.userId !== String(userId) || intent.deviceId !== deviceId) {
                return {
                    success: false,
                    error: "Onchain write request does not match this device's Write action",
                    retryable: false,
                }
            }
            const walletText = await recoverLocalWalletTextWithShares(
                userId,
                serverShareResult.treechatShare,
                clientRecord.clientShare,
            )
            const wallet = JSON.parse(walletText)
            if (!wallet.payPk) {
                return {
                    success: false,
                    error: 'Local wallet is missing its payment signing key',
                    retryable: false,
                }
            }
            // The record's file streams from the server in bounded chunks so the signature
            // commits to the exact bytes that will be broadcast.
            const content = {
                mode: 'remote_chunks' as const,
                size: manifest.contentBytes,
                type: 'application/octet-stream',
                readChunk: (start: number, end: number) =>
                    api.getMintMediaContentChunk(
                        manifest.answerId,
                        manifest.attachmentId,
                        start,
                        end - start,
                    ),
            }
            const signatures = await signOwnerAttachmentWrite(
                manifest,
                intent,
                content,
                wallet.payPk,
            )
            const submitted = await api.submitOrdinalSignatures(requestId, deviceId, signatures)
            if (submitted.success) {
                clearOwnerAttachmentWriteIntent(manifest.answerId, intent.attachmentId)
                return { success: true }
            }
            return {
                success: false,
                error: submitted.error || 'Failed to submit onchain write signatures',
                retryable: true,
            }
        }

        if (request.tx_type === 'ordinal_send') {
            const manifest = request.ordinal_manifest as OrdinalTransferManifest | undefined
            if (!manifest || manifest.signingRequestId !== requestId) {
                return {
                    success: false,
                    error: 'Ordinal signing manifest is missing or mismatched',
                    retryable: false,
                }
            }
            const manifestOutpoint = `${manifest.ordinal.txid}:${manifest.ordinal.vout}`
            const intent = request.ordinal_id
                ? getOrdinalSendIntent(
                      request.ordinal_id,
                      manifest.recipientAddress,
                      manifestOutpoint,
                  )
                : null
            const expectedOutpoint = intent?.ordinalOutpoint.replace(/_([0-9]+)$/, ':$1')
            if (
                !intent ||
                intent.userId !== String(userId) ||
                intent.deviceId !== deviceId ||
                intent.recipientAddress !== manifest.recipientAddress ||
                (expectedOutpoint &&
                    expectedOutpoint !== `${manifest.ordinal.txid}:${manifest.ordinal.vout}`)
            ) {
                return {
                    success: false,
                    error: "Ordinal signing request does not match this device's send action",
                    retryable: false,
                }
            }
            const walletText = await recoverLocalWalletTextWithShares(
                userId,
                serverShareResult.treechatShare,
                clientRecord.clientShare,
            )
            const wallet = JSON.parse(walletText)
            if (!wallet.payPk || !wallet.ordPk) {
                return {
                    success: false,
                    error: 'Local wallet is missing ordinal signing keys',
                    retryable: false,
                }
            }
            // The server resolves the outpoint from this request's frozen manifest;
            // the streamed bytes must hash to that input's scriptSha256.
            const ordinalPrevoutScript = await api.openSigningRequestOrdinalPrevoutScript(
                requestId,
                deviceId,
            )
            const signatures = await signOrdinalTransfer({
                manifest,
                ordinalPrevoutScript,
                payPk: wallet.payPk,
                ordPk: wallet.ordPk,
                expectedUserId: String(userId),
                expectedDeviceId: deviceId,
            })
            const submitted = await api.submitOrdinalSignatures(requestId, deviceId, signatures)
            if (submitted.success) {
                clearOrdinalSendIntent(
                    request.ordinal_id!,
                    manifest.recipientAddress,
                    manifestOutpoint,
                )
            }
            return submitted.success
                ? { success: true }
                : {
                      success: false,
                      error: submitted.error || 'Failed to submit ordinal signatures',
                      retryable: true,
                  }
        }

        // Step 3: Recover the local wallet from the share pair, then read payPk from it
        const wif = await recoverPaymentKeyWifWithShares(
            userId,
            serverShareResult.treechatShare,
            clientRecord.clientShare,
            serverShareResult.address,
        )

        // Step 4: Sign the exact unsigned transaction template from the server
        if (!request.unsigned_tx_template) {
            return {
                success: false,
                error: 'Signing request is missing unsigned_tx_template',
                retryable: false,
            }
        }

        const signResult = await signTransaction(request.unsigned_tx_template, wif)

        // Note: In JS we can't truly zeroize memory, but we let wif go out of scope immediately
        // The private key is only in memory briefly during signing

        if (!signResult.success || !signResult.rawTx) {
            return {
                success: false,
                error: signResult.error || 'Failed to sign transaction',
                retryable: false,
            }
        }

        console.log(`>>> Signed transaction for request ${requestId}, submitting...`)

        // Step 6: Submit signed transaction back to server
        const submitResult = await api.submitSignedTx(requestId, deviceId, signResult.rawTx)
        if (!submitResult.success) {
            return {
                success: false,
                error: submitResult.error || 'Failed to submit signed transaction',
                retryable: true,
            }
        }

        console.log(`>>> Submitted signed transaction for request ${requestId}`)
        return { success: true }
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        console.error(`>>> Error processing signing request ${requestId}:`, errorMessage)
        return {
            success: false,
            error: errorMessage,
            retryable: !isSelfCustodyReinitializationRequiredError(error),
        }
    } finally {
        processingRequests.delete(requestId)
    }
}

// ============================================================================
// Polling Fallback
// ============================================================================

/**
 * Starts polling for pending signing requests.
 * Called on app startup and when ActionCable reconnects.
 */
export async function startSigningRequestPolling(): Promise<void> {
    // Don't start multiple polling loops
    if (pollingIntervalId !== null) {
        return
    }

    if (!hasClientSigningFeature()) {
        return
    }

    // Check if user has client signing set up
    const userId = gon.currentUser?.id
    if (!userId) {
        return
    }

    const clientRecord = await getClientShare(userId)
    if (!clientRecord) {
        // No client share = no client signing = no need to poll
        return
    }

    console.log('>>> Starting signing request polling')

    // Poll immediately on start
    await checkPendingSigningRequests(clientRecord)

    // Then poll periodically
    pollingIntervalId = setInterval(async () => {
        await checkPendingSigningRequests(clientRecord)
    }, POLLING_INTERVAL_MS)
}

/**
 * Stops polling for signing requests.
 */
export function stopSigningRequestPolling(): void {
    if (pollingIntervalId !== null) {
        clearInterval(pollingIntervalId)
        pollingIntervalId = null
        console.log('>>> Stopped signing request polling')
    }
}

/**
 * Checks for and processes any pending signing requests.
 */
async function checkPendingSigningRequests(clientRecord: ClientShareRecord): Promise<void> {
    try {
        const requests = await api.getSigningRequests(clientRecord.deviceId)

        if (requests.length === 0) {
            return
        }

        console.log(`>>> Found ${requests.length} pending signing request(s)`)

        for (const request of requests) {
            await processSigningRequest(request)
        }
    } catch (error) {
        console.error('>>> Error checking pending signing requests:', error)
    }
}

// ============================================================================
// Exports for testing/debugging
// ============================================================================

/**
 * Check if client signing is available for the current user.
 * Returns the client share record if available, null otherwise.
 */
export async function getClientSigningStatus(): Promise<{
    available: boolean
    deviceId?: string
    address?: string
}> {
    const userId = gon.currentUser?.id
    if (!userId) {
        return { available: false }
    }

    if (!hasClientSigningFeature()) {
        return { available: false }
    }

    const clientRecord = await getClientShare(userId)
    if (!clientRecord) {
        return { available: false }
    }

    return {
        available: true,
        deviceId: clientRecord.deviceId,
        address: clientRecord.address,
    }
}
